
import { jitsiLocalStorage } from '@jitsi/js-utils';
import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import TextareaAutosize from 'react-textarea-autosize';
import type { Dispatch } from 'redux';

import { translate } from '../../../base/i18n';
import { Icon, IconAttachFile, IconPlane, IconSpinner } from '../../../base/icons';
import { connect } from '../../../base/redux';
import { getBackendSafeRoomName } from '../../../base/util';
import { showErrorNotification, showWarningNotification } from '../../../notifications';
import { getUploadFormData } from '../../functions';

import SmileysPanel from './SmileysPanel';


/**
 * The type of the React {@code Component} props of {@link ChatInput}.
 */
type Props = {

    /**
     * Ref to the chat panel DOM node.
     */
    chatPanelNode: ?HTMLElement,

    /**
     * Whether the local user's chat is suspended by a moderator (visitors only).
     */
    _chatSuspended: boolean,

    /**
     * Invoked to send chat messages.
     */
    dispatch: Dispatch<any>,

    /**
     * Optional callback to invoke when the chat textarea has auto-resized to
     * fit overflowing text.
     */
    onResize: ?Function,

    /**
     * Optional callback to invoke when typing.
     */
    onType: ?Function,

    /**
     * Callback to invoke on message send.
     */
    onSend: Function,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function,

    /**
     * Access token for API calls.
     */
    accesTokn: string,

    /**
     * Client ID for MH.
     */
    MH_CLIENT_ID: string,

    /**
     * Meeting ID.
     */
    MeetingID: string,

    /**
     * User email.
     */
    email: ?string,

    /**
     * Domain.
     */
    domain: string
};

/**
 * The type of the React {@code Component} state of {@link ChatInput}.
 */
type State = {

    /**
     * User provided nickname when the input text is provided in the view.
     */
    message: string,

    /**
     * Whether or not the smiley selector is visible.
     */
    showSmileysPanel: boolean,

    /**
     * Selected files for upload.
     */
    selectedFiles: Array<File>,

    /**
     * Whether file upload is in progress.
     */
    isUploading: boolean,

    /**
     * Whether user is dragging file over the chat panel.
     */
    isDraggingOver: boolean,

    /**
     * Upload progress percentage.
     */
    uploadProgress: number
};

/**
 * Implements a React Component for drafting and submitting a chat message.
 *
 * @augments Component
 */
class ChatInput extends PureComponent<Props, State> {
    _fileInput: ?HTMLInputElement;
    _textArea: ?HTMLTextAreaElement;
    _uploadXhr: ?XMLHttpRequest;

    state = {
        message: '',
        showSmileysPanel: false,
        selectedFiles: [],
        isUploading: false,
        isDraggingOver: false,
        uploadProgress: 0
    };

    /**
     * Initializes a new {@code ChatInput} instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        this._fileInput = null;
        this._textArea = null;
        this._uploadXhr = null;

        // Bind event handlers so they are only bound once for every instance.
        this._onAttachFileClick = this._onAttachFileClick.bind(this);
        this._onAttachFileKeyDown = this._onAttachFileKeyDown.bind(this);
        this._setFileInputRef = this._setFileInputRef.bind(this);
        this._onDetectSubmit = this._onDetectSubmit.bind(this);
        this._onMessageChange = this._onMessageChange.bind(this);
        this._onSmileySelect = this._onSmileySelect.bind(this);
        this._onSubmitMessage = this._onSubmitMessage.bind(this);
        this._onToggleSmileysPanel = this._onToggleSmileysPanel.bind(this);
        this._setTextAreaRef = this._setTextAreaRef.bind(this);
        this._onHandleFileChange = this._onHandleFileChange.bind(this);
        this._handleUpload = this._handleUpload.bind(this);
        this._onDragOver = this._onDragOver.bind(this);
        this._onDragEnter = this._onDragEnter.bind(this);
        this._onDragLeave = this._onDragLeave.bind(this);
        this._onDrop = this._onDrop.bind(this);
        this._onCancelUpload = this._onCancelUpload.bind(this);
    }

    /**
     * Implements React's {@link Component#componentDidMount()}.
     *
     * @inheritdoc
     */
    componentDidMount() {
        /**
         * HTML Textareas do not support autofocus. Simulate autofocus by
         * manually focusing.
         */
        this._focus();

        const chatPanel = document.getElementById('chat-panel');

        if (chatPanel) {
            chatPanel.style.position = 'relative';
            chatPanel.addEventListener('dragover', this._onDragOver);
            chatPanel.addEventListener('dragenter', this._onDragEnter);
            chatPanel.addEventListener('dragleave', this._onDragLeave);
            chatPanel.addEventListener('drop', this._onDrop);
        }
    }

    /**
     * Implements React's {@link Component#componentWillUnmount()}.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        const chatPanelNode = this.props.chatPanelNode;
        const chatPanel = document.getElementById('chat-panel');

        if (chatPanelNode) {
            chatPanelNode.removeEventListener('dragover', this._onDragOver);
            chatPanelNode.removeEventListener('dragenter', this._onDragEnter);
            chatPanelNode.removeEventListener('dragleave', this._onDragLeave);
            chatPanelNode.removeEventListener('drop', this._onDrop);
        }

        if (chatPanel) {
            chatPanel.removeEventListener('dragover', this._onDragOver);
            chatPanel.removeEventListener('dragenter', this._onDragEnter);
            chatPanel.removeEventListener('dragleave', this._onDragLeave);
            chatPanel.removeEventListener('drop', this._onDrop);
        }
    }

    /**
     * Implements React's {@link Component#componentDidUpdate()}.
     *
     * @inheritdoc
     */
    componentDidUpdate(prevProps) {
        if (this.props.chatPanelNode && this.props.chatPanelNode !== prevProps.chatPanelNode) {
            const chatPanel = this.props.chatPanelNode;

            chatPanel.style.position = 'relative';
            chatPanel.addEventListener('dragover', this._onDragOver);
            chatPanel.addEventListener('dragenter', this._onDragEnter);
            chatPanel.addEventListener('dragleave', this._onDragLeave);
            chatPanel.addEventListener('drop', this._onDrop);
        }
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const smileysPanelClassName = `${this.state.showSmileysPanel
            ? 'show-smileys' : 'hide-smileys'} smileys-panel`;
        const display = { display: 'none' };

        const overlayStyle = {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 10001,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            border: '2px dashed #4b6790',
            borderRadius: '0px 0px 20px 20px',
            pointerEvents: 'none'
        };

        const st_1 = { marginTop: '10px',
            fontSize: '16px',
            color: '#4b6790',
            fontWeight: 'bold' };

        const chatPanel = this.props.chatPanelNode
            || (this._textArea && this._textArea.closest('#chat-panel'))
            || document.getElementById('chat-panel');

        const uploadProgressOverlay = this.state.isUploading && (
            <div className = 'upload-progress-overlay'>
                <div className = 'upload-spinner-container'>
                    <span className = 'upload-spinner'>
                        <Icon
                            color = '#4b6790'
                            size = { 36 }
                            src = { IconSpinner } />
                    </span>
                </div>
                <div className = 'upload-file-name'>
                    {this.state.selectedFiles.map(f => f.name).join(', ')}
                </div>
                <div className = 'upload-progress-track'>
                    <div
                        className = 'upload-progress-bar'
                        style = {{ width: `${this.state.uploadProgress}%` }} />
                </div>
                <div className = 'upload-progress-percent'>
                    {this.props.t('chat.fileUpload.uploading', { fileName: this.state.selectedFiles[0]?.name }) || 'Uploading...' } {this.state.uploadProgress}%
                </div>
                <button
                    className = 'upload-cancel-btn'
                    onClick = { this._onCancelUpload }
                    type = 'button'>
                    {this.props.t('dialog.Cancel') || 'Cancel'}
                </button>
            </div>
        );

        return (
            <div className = { `chat-input-container${this.state.message.trim().length ? ' populated' : ''}` }>
                {this.state.isDraggingOver && chatPanel && ReactDOM.createPortal(
                    <div style = { overlayStyle }>
                        <Icon
                            color = '#4b6790'
                            size = { 48 }
                            src = { IconAttachFile } />
                        <span
                            style = { st_1 }>
                            {this.props.t('chat.fileUpload.dropFilesHere') || 'Drop files here to upload'}
                        </span>
                    </div>,
                    chatPanel
                )}
                {uploadProgressOverlay && (
                    chatPanel ? ReactDOM.createPortal(
                        uploadProgressOverlay,
                        chatPanel
                    ) : uploadProgressOverlay
                )}
                <div id = 'chat-input'>
                    <div className = 'usrmsg-form'>
                        <TextareaAutosize
                            disabled = { this.state.isUploading || this.props._chatSuspended }
                            id = 'usermsg'
                            inputRef = { this._setTextAreaRef }
                            maxLength = { 1000 }
                            maxRows = { 10 }
                            onChange = { this._onMessageChange }
                            onHeightChange = { this.props.onResize }
                            onKeyDown = { this._onDetectSubmit }
                            placeholder = { this.props.t(this.props._chatSuspended
                                ? 'chat.suspendedByModerator'
                                : 'chat.messagebox') }
                            value = { this.state.message } />
                        <div className = 'usermsg-length'>
                            {this.state.message.length} / 1000
                        </div>
                    </div>
                    {/*
                    <div
                        className = 'chat-button'
                        onClick = { () => this._onToggleSmileysPanel() }>
                        <Icon
                            size = { 18 }
                            src = { IconEmoji } />
                    </div> */}
                    <div>
                        <div
                            aria-label = { this.props.t('chat.fileUpload.attachFile') || 'Attach file' }
                            className = { `chat-button attach-file ${this.state.isUploading ? 'uploading' : ''}` }
                            onClick = { this._onAttachFileClick }
                            onKeyDown = { this._onAttachFileKeyDown }
                            role = 'button'
                            tabIndex = { 0 }>
                            {this.state.isUploading ? (
                                <span className = 'upload-spinner'>
                                    <Icon src = { IconSpinner } />
                                </span>
                            ) : <Icon src = { IconAttachFile } />}
                        </div>
                        <input
                            // eslint-disable-next-line
                            accept = '.aac,.avi,.avchd,.bmp,.csv,.doc,.docx,.eps,.f4v,.flac,.flv,.gif,.ico,.jpeg,.jpg,.json,.log,.m4a,.md,.mkv,.mov,.mp3,.mp4,.mpeg4,.odp,.odt,.ogg,.pdf,.png,.ppt,.pptx,.psd,.rar,.rtf,.svg,.swf,.tif,.tiff,.txt,.wav,.webm,.webp,.wma,.wmv,.xls,.xlsx,.xml,.zip,.7z'
                            disabled = { this.state.isUploading }
                            id = 'file-input'
                            multiple = { false }
                            onChange = { this._onHandleFileChange }
                            ref = { this._setFileInputRef }
                            style = { display }
                            type = 'file' />
                    </div>

                    <div className = { smileysPanelClassName }>
                        <SmileysPanel
                            onSmileySelect = { this._onSmileySelect } />
                    </div>
                    <div
                        className = 'chat-button'
                        onClick = { this._onSubmitMessage }>
                        <Icon
                            size = { 18 }
                            src = { IconPlane } />
                    </div>

                </div>
            </div>
        );
    }

    /**
     * Place cursor focus on this component's text area.
     *
     * @private
     * @returns {void}
     */
    _focus() {
        this._textArea && this._textArea.focus();
    }

    _onAttachFileClick: () => void;

    /**
     * Triggers file selection dialog.
     *
     * @private
     * @returns {void}
     */
    _onAttachFileClick() {
        if (this.state.isUploading) {
            return;
        }

        if (this._fileInput) {
            this._fileInput.value = '';
            this._fileInput.click();
        }
    }

    _onAttachFileKeyDown: (Object) => void;

    /**
     * Handles keyboard activation for attach file button.
     *
     * @param {Object} event - The keyboard event.
     * @private
     * @returns {void}
     */
    _onAttachFileKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this._onAttachFileClick();
        }
    }

    _setFileInputRef: (?HTMLInputElement) => void;

    /**
     * Sets the ref to the file input element.
     *
     * @param {?HTMLInputElement} element - The file input element.
     * @private
     * @returns {void}
     */
    _setFileInputRef(element) {
        this._fileInput = element;
    }

    /**
     * Handles drag over event to allow dropping files for upload.
     * @param {*} event - The drag over event.
     */
    _onDragOver(event) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.state.isDraggingOver) {
            this.setState({ isDraggingOver: true });
        }
    }

    /**
     * Handles drag enter event to show visual feedback for file drop.
     * @param {*} event - The drag enter event.
     */
    _onDragEnter(event) {
        event.preventDefault();
        event.stopPropagation();

        this._dragCounter = (this._dragCounter || 0) + 1;
        if (this._dragCounter === 1) {
            this.setState({ isDraggingOver: true });
        }
    }

    /**
     * Handles drag leave event to hide visual feedback when file is dragged away.
     * @param {*} event - The drag leave event.
     */
    _onDragLeave(event) {
        event.preventDefault();
        event.stopPropagation();

        this._dragCounter = (this._dragCounter || 0) - 1;
        if (this._dragCounter <= 0) {
            this._dragCounter = 0;
            this.setState({ isDraggingOver: false });
        }
    }

    /**
     * Handles drop event to process files for upload.
     * @param {*} event - The drop event.
     * @returns {void}
     */
    _onDrop(event) {
        event.preventDefault();
        event.stopPropagation();
        this._dragCounter = 0;
        this.setState({ isDraggingOver: false });

        if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            const files = Array.from(event.dataTransfer.files);

            const validationResult = this._validateFiles(files);

            if (!validationResult.isValid) {
                this.props.dispatch(showErrorNotification({
                    titleKey: 'chat.fileUpload.failed',
                    descriptionKey: 'chat.fileUpload.failedDescription',
                    descriptionArguments: { error: validationResult.error }
                }));

                return;
            }

            this.setState({
                selectedFiles: files,
                isUploading: true,
                uploadProgress: 0
            }, () => {
                this._handleUpload(files);
            });
        }
    }

    _onSubmitMessage: () => void;

    /**
     * Submits the message to the chat window.
     *
     * @returns {void}
     */
    _onSubmitMessage() {
        if (this.props._chatSuspended) {
            return;
        }

        const trimmed = this.state.message.trim();

        if (trimmed) {
            const now = Date.now();

            this._lastSendTimes = (this._lastSendTimes || []).filter(t => now - t < 10000); // last 10 seconds
            const titleKey = this.props.t('chat.chatRateLimitKey');
            const descriptionKey = this.props.t('chat.chatRateLimitMessage');

            if (this._lastSendTimes.length >= 5) {
                // Rate limit: 5 messages per 10 seconds
                this.props.dispatch(showWarningNotification({
                    titleKey,
                    description: descriptionKey
                }));

                return;
            }

            this._lastSendTimes.push(now);

            this.props.onSend(trimmed);
            this.setState({ message: '' });
        }
    }

    _onHandleFileChange: (Object) => void;

    /**
     * Handles file selection and triggers upload.
     *
     * @param {Object} event - File input change event.
     * @private
     * @returns {void}
     */
    _onHandleFileChange(event) {
        const fileInput = event.target;
        const files = Array.from(fileInput.files || []);

        if (files.length > 0) {
            // Validate files before setting state
            const validationResult = this._validateFiles(files);

            if (!validationResult.isValid) {
                this.props.dispatch(showErrorNotification({
                    titleKey: 'chat.fileUpload.failed',
                    descriptionKey: 'chat.fileUpload.failedDescription',
                    descriptionArguments: { error: validationResult.error }
                }));

                fileInput.value = '';

                return;
            }

            this.setState({
                selectedFiles: files,
                isUploading: true,
                uploadProgress: 0
            }, () => {
                // Automatically trigger upload when files are selected
                this._handleUpload(files);
            });
        }
    }

    _validateFiles: (Object) => void;

    /**
     * Validates selected files against size and type restrictions.
     *
     * @param {Array<File>} files - Array of files to validate.
     * @private
     * @returns {Object} Validation result with isValid flag and error message.
     */
    _validateFiles(files) {
        // Default limits if not configured
        const maxFileSize = 100 * 1024 * 1024; // 100MB
        const maxTotalSize = 500 * 1024 * 1024; // 500MB
        const maxFileCount = 10;
        const supportedExtensions = [
            'aac', 'avi', 'avchd', 'bmp', 'csv', 'doc', 'docx', 'eps',
            'f4v', 'flac', 'flv', 'gif', 'ico', 'jpeg', 'jpg', 'json',
            'log', 'm4a', 'md', 'mkv', 'mov', 'mp3', 'mp4', 'mpeg4',
            'odp', 'odt', 'ogg', 'pdf', 'png', 'ppt', 'pptx', 'psd',
            'rar', 'rtf', 'svg', 'swf', 'tif', 'tiff', 'txt', 'wav',
            'webm', 'webp', 'wma', 'wmv', 'xls', 'xlsx', 'xml', 'zip', '7z'
        ];

        // Check file count
        if (files.length > maxFileCount) {
            return {
                isValid: false,
                error: this.props.t('chat.fileUpload.tooManyFiles', { max: maxFileCount })
            };
        }

        let totalSize = 0;

        // Check each file
        for (const file of files) {
            // Check individual file size
            if (file.size > maxFileSize) {
                const maxSizeFormatted = this._formatFileSize(maxFileSize);
                const fileSizeFormatted = this._formatFileSize(file.size);

                return {
                    isValid: false,
                    error: this.props.t('chat.fileUpload.fileTooLarge', {
                        fileName: file.name,
                        fileSize: fileSizeFormatted,
                        maxSize: maxSizeFormatted
                    })
                };
            }

            // Check file type if restrictions are configured
            const fileExtension = file.name.split('.').pop()
.toLowerCase();

            if (!supportedExtensions.includes(fileExtension)) {
                return {
                    isValid: false,
                    error: `File type .${fileExtension} is not supported.`
                };
            }

            totalSize += file.size;
        }

        // Check total size
        if (totalSize > maxTotalSize) {
            const maxTotalFormatted = this._formatFileSize(maxTotalSize);
            const totalSizeFormatted = this._formatFileSize(totalSize);

            return {
                isValid: false,
                error: this.props.t('chat.fileUpload.totalSizeTooLarge', {
                    totalSize: totalSizeFormatted,
                    maxTotal: maxTotalFormatted
                })
            };
        }

        return { isValid: true };
    }

    _formatFileSize: (number) => string;

    /**
     * Formats file size in bytes to human readable format.
     *
     * @param {number} bytes - File size in bytes.
     * @private
     * @returns {string} Formatted file size string.
     */
    _formatFileSize(bytes) {
        if (bytes === 0) {
            return '0 Bytes';
        }

        const k = 1024;
        const sizes = [ 'Bytes', 'KB', 'MB', 'GB' ];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
    }

    _onCancelUpload: () => void;

    /**
     * Cancels the active file upload request.
     */
    _onCancelUpload() {
        if (this._uploadXhr) {
            this._uploadXhr.abort();
            this._uploadXhr = null;
        }
        this.setState({
            isUploading: false,
            uploadProgress: 0,
            selectedFiles: []
        });

        if (this._fileInput) {
            this._fileInput.value = '';
        }
    }

    _handleUpload: (?Array<Object>) => Promise<void>;

    /**
     * Handles file upload to the server.
     *
     * @param {Array<Object>} [filesToUpload] - The files to upload.
     * @private
     * @returns {Promise<void>}
     */
    async _handleUpload(filesToUpload: ?Array<Object>) {
        const files = filesToUpload || this.state.selectedFiles;

        if (!files || files.length === 0) {
            this.setState({
                isUploading: false,
                uploadProgress: 0,
                selectedFiles: []
            });

            return;
        }

        this.setState({
            selectedFiles: files,
            isUploading: true,
            uploadProgress: 0
        });

        const uploadStartTime = Date.now();

        try {
            const payloadData = {
                mhClientID: this.props.MH_CLIENT_ID,
                meetingID: this.props.MeetingID,
                email: this.props.email,
                accessToken: this.props.accesTokn,
                config: this.props.config,
                files: []
            };

            // Append all selected files
            files.forEach(file => {
                payloadData.files.push(file);
            });

            const formData = getUploadFormData(payloadData);

            const { API_URL_PREFIX, API_VERSION_PREFIX } = this.props.config;
            const uploadUrl = `${API_URL_PREFIX}${API_VERSION_PREFIX}customer/savemeetinguploadedfile`;

            const response = await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                this._uploadXhr = xhr;

                xhr.open('POST', uploadUrl, true);
                xhr.setRequestHeader('Authorization', `Bearer ${this.props.accesTokn}`);

                // Track upload progress
                xhr.upload.addEventListener('progress', event => {
                    if (event.lengthComputable) {
                        const percent = Math.min(95, Math.round((event.loaded / event.total) * 100));

                        this.setState({ uploadProgress: percent });
                    }
                });

                xhr.onload = () => {
                    this._uploadXhr = null;
                    if (xhr.status >= 200 && xhr.status < 300) {
                        try {
                            const resObj = JSON.parse(xhr.responseText);

                            resolve(resObj);
                        } catch {
                            reject(new Error('Failed to parse upload response'));
                        }
                    } else {
                        reject(new Error(`HTTP error! Status: ${xhr.status}`));
                    }
                };

                xhr.onerror = () => {
                    this._uploadXhr = null;
                    reject(new Error('Network connection error occurred during upload'));
                };

                xhr.onabort = () => {
                    this._uploadXhr = null;
                    reject(new Error('Upload cancelled by user'));
                };

                xhr.send(formData);
            });

            // Show 100% completion
            this.setState({ uploadProgress: 100 });

            // Ensure overlay displays long enough for the user to perceive progress completion
            const elapsed = Date.now() - uploadStartTime;
            const remainingWait = Math.max(400, 700 - elapsed);

            await new Promise(resolve => setTimeout(resolve, remainingWait));

            if (response?.success) {
                // Process each uploaded file
                files.forEach(file => {
                    const fileName = file.name;

                    // Create file link message with better styling
                    const fileMessage
                    = `<a href="${response.download_url
                        || response.file_url}" download="${fileName}" class="file-attachment" style="display: inline-flex; align-items: center; 
                        text-decoration: none; color: #007bff; padding: 6px 10px; border: 1px solid #ddd; border-radius: 6px; background: #f8f9fa; margin: 2px; font-size: 14px; 
                        max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    ${fileName}
                    </a>`;

                    // Automatically send the file message immediately
                    this.props.onSend(fileMessage);
                });
            } else {
                const errorMessage = response?.message || `${this.props.t('localRecording.clientState.unknown')} ${this.props.t('connection.ERROR')}`;

                // Show error notification instead of chat message
                this.props.dispatch(showErrorNotification({
                    titleKey: 'chat.fileUpload.failed',
                    descriptionKey: 'chat.fileUpload.failedDescription',
                    descriptionArguments: { error: errorMessage }
                }));
            }

        } catch (error) {
            if (error?.message !== 'Upload cancelled by user') {
                const errorMessage = error?.message || `${this.props.t('localRecording.clientState.unknown')} ${this.props.t('connection.ERROR')}`;

                // Show error notification instead of chat message
                this.props.dispatch(showErrorNotification({
                    titleKey: 'chat.fileUpload.error',
                    descriptionKey: 'chat.fileUpload.errorDescription',
                    descriptionArguments: { error: errorMessage }
                }));
            }
        } finally {
            this.setState({
                isUploading: false,
                uploadProgress: 0,
                selectedFiles: []
            });

            if (this._fileInput) {
                this._fileInput.value = '';
            }
        }
    }

    _onDetectSubmit: (Object) => void;

    /**
     * Detects if enter has been pressed. If so, submit the message in the chat
     * window.
     *
     * @param {string} event - Keyboard event.
     * @private
     * @returns {void}
     */
    _onDetectSubmit(event) {
        if (event.keyCode === 13 && event.shiftKey === false && !this.state.isUploading) {
            event.preventDefault();
            this._onSubmitMessage();
        }
    }

    _onMessageChange: (Object) => void;

    /**
     * Updates the known message the user is drafting.
     *
     * @param {string} event - Keyboard event.
     * @private
     * @returns {void}
     */
    _onMessageChange(event) {
        this.setState({ message: event.target.value });
        if (this.props.onType) {
            this.props.onType();
        }
    }

    _onSmileySelect: (string) => void;

    /**
     * Appends a selected smileys to the chat message draft.
     *
     * @param {string} smileyText - The value of the smiley to append to the
     * chat message.
     * @private
     * @returns {void}
     */
    _onSmileySelect(smileyText) {
        this.setState({
            message: `${this.state.message} ${smileyText}`,
            showSmileysPanel: false
        });

        this._focus();

        if (this.props.onType) {
            this.props.onType();
        }
    }

    _onToggleSmileysPanel: () => void;

    /**
     * Callback invoked to hide or show the smileys selector.
     *
     * @private
     * @returns {void}
     */
    _onToggleSmileysPanel() {
        this.setState({ showSmileysPanel: !this.state.showSmileysPanel });
        this._focus();
    }

    _setTextAreaRef: (?HTMLTextAreaElement) => void;

    /**
     * Sets the reference to the HTML TextArea.
     *
     * @param {HTMLTextAreaElement} textAreaElement - The HTML text area element.
     * @private
     * @returns {void}
     */
    _setTextAreaRef(textAreaElement: ?HTMLTextAreaElement) {
        this._textArea = textAreaElement;
    }
}

/** * Maps (parts of) the Redux state to the associated props for the
 * {@code ChatInput} component.
 *
 * @param {Object} state - The Redux state.
 * @returns {Object}
 */
export function _mapStateToProps(state: Object) {
    const { accessToken } = state['features/base/jwt'];
    const userAccessToken = jitsiLocalStorage.getItem('accessToken');
    const accesTokn = accessToken ?? userAccessToken;
    const { MH_CLIENT_ID } = state['features/base/config'];
    const MeetingID = getBackendSafeRoomName(state['features/base/conference'].room);
    const { email } = state['features/base/settings'];
    const domain = state['features/base/config'].hosts.domain;
    const config = state['features/base/config'];

    return {
        accesTokn,
        MH_CLIENT_ID,
        MeetingID,
        email,
        domain,
        config,
        _chatSuspended: Boolean(
            state['features/visitors']?.iAmVisitor && state['features/visitors']?.iAmChatSuspended)
    };
}

export default connect(_mapStateToProps)(translate(ChatInput));
